import React, { useContext } from 'react';
import { LayoutDashboard, Box, Wrench, FileSpreadsheet, FolderSearch, Tag, ShoppingCart, Layers, CreditCard, Shield, Target } from 'lucide-react';
import '../../index.css';
import { Link } from 'react-router-dom';
import { AuthContext } from '../Context/AuthenticationContext';

const Dashboard = () => {
    const { accUsername } = useContext(AuthContext);

    // Sample Collections 配置
    const sampleCollections = [
        {
            group: "Home",
            priceListPath: "/sample-collections/home/pricelist",
            cardViewPath: "/sample-collections/home/cardview"
        },
        {
            group: "Apparel",
            priceListPath: "/sample-collections/apparel/pricelist",
            cardViewPath: "/sample-collections/apparel/cardview"
        },
        {
            group: "Accessory",
            priceListPath: "/sample-collections/accessory/pricelist",
            cardViewPath: "/sample-collections/accessory/cardview"
        },
        {
            group: "Baby Home",
            priceListPath: "/sample-collections/baby-home/pricelist",
            cardViewPath: "/sample-collections/baby-home/cardview"
        }
    ];

    // Licensed Brand 配置
    const licensedBrands = [
        {
            name: "Vitality",
            path: "/licensed-brand/vitality"
        },
        {
            name: "Lego",
            path: "/licensed-brand/lego"
        }
        // 未来可扩展更多品牌
    ];

    // 右侧
    const rightTools = [
        {
            name: "Account Contacts",
            path: "/contact",
            icon: <Wrench className="w-6 h-6" />,
            description: "View and manage account contacts"
        },
        {
            name: "Target Dashboard",
            path: "/target-dashboard",
            icon: <Target className="w-6 h-6" />,
            description: "Target Retail Analytics Dashboard"
        },
        {
            name: "Amazon Best Seller",
            path: "/amazon-best-seller",
            icon: <FileSpreadsheet className="w-6 h-6" />,
            description: "View Amazon Best Sellers"
        },
        {
            name: "Warehouse Inventory",
            path: "/inventory",
            icon: <Box className="w-6 h-6" />,
            description: "Track and view warehouse inventory"
        },
        {
            name: "QR Scanner",
            path: "/qr-scanner",
            icon: <FolderSearch className="w-6 h-6" />,
            description: "Scan QR code to see product pricing and details"
        },
        {
            name: "Previous Order",
            path: "/previous-order",
            icon: <ShoppingCart className="w-6 h-6" />,
            description: "View previous orders"
        }
    ];

    return (
        <div 
            className="min-h-screen" 
            style={{ 
                backgroundColor: '#f5f5f7', 
                padding: '32px 24px',
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
            }}
        >
            <div className="max-w-7xl mx-auto">
                <div className="flex items-center justify-between gap-4 mb-8">
                    <div className="flex items-center gap-3">
                        <img 
                            src="/Assets/sunwin_logo.png" 
                            alt="Sunwin Logo" 
                            style={{ height: '40px', width: 'auto' }} 
                        />
                        <span 
                            className="text-3xl" 
                            style={{ 
                                color: '#1d1d1f',
                                fontWeight: '600',
                                letterSpacing: '-0.02em',
                                lineHeight: '1.2'
                            }}
                        >
                            Sunwin NYC Tools
                        </span>
                    </div>
                    {accUsername && (
                        <div style={{
                            color: '#6e6e73',
                            fontWeight: '500',
                            fontSize: '15px',
                            letterSpacing: '-0.01em'
                        }}>
                            👤 {accUsername}
                        </div>
                    )}
                </div>
                <div className="flex flex-col md:flex-row gap-6 items-stretch">
                    {/* 左侧 */}
                    <div className="flex-1 flex flex-col">
                        <div 
                            className="p-6 rounded-2xl flex-1 flex flex-col" 
                            style={{ 
                                backgroundColor: '#ffffff', 
                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04), 0 1px 2px rgba(0, 0, 0, 0.06)',
                                border: 'none'
                            }}
                        >
                            <h2 
                                className="text-xl mb-5" 
                                style={{ 
                                    color: '#1d1d1f',
                                    fontWeight: '600',
                                    letterSpacing: '-0.01em',
                                    lineHeight: '1.4'
                                }}
                            >
                                Sample Collections
                            </h2>
                            <div className="flex flex-col gap-5">
                                {sampleCollections.map((item) => (
                                    <div key={item.group}>
                                        <div 
                                            className="mb-2.5 flex items-center gap-2" 
                                            style={{ 
                                                color: '#0071e3',
                                                fontWeight: '600',
                                                fontSize: '16px',
                                                letterSpacing: '-0.01em'
                                            }}
                                        >
                                            <Layers className="w-5 h-5" style={{ strokeWidth: '1.5' }} />
                                            {item.group}
                                        </div>
                                        <div className="flex gap-2">
                                            <Link
                                                to={item.priceListPath}
                                                className="group flex items-center gap-2 px-4 py-2.5 rounded-xl
                                                           hover:-translate-y-0.5
                                                           transition-all duration-300 ease-out"
                                                style={{ 
                                                    backgroundColor: '#f5f5f7',
                                                    color: '#1d1d1f',
                                                    border: 'none',
                                                    fontWeight: '500',
                                                    fontSize: '15px',
                                                    letterSpacing: '-0.01em'
                                                }}
                                                onMouseEnter={(e) => {
                                                    e.currentTarget.style.backgroundColor = '#0071e3';
                                                    e.currentTarget.style.color = '#ffffff';
                                                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 113, 227, 0.3)';
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.currentTarget.style.backgroundColor = '#f5f5f7';
                                                    e.currentTarget.style.color = '#1d1d1f';
                                                    e.currentTarget.style.boxShadow = 'none';
                                                }}
                                            >
                                                <FileSpreadsheet className="w-5 h-5 group-hover:scale-110 transition-transform duration-300" style={{ strokeWidth: '1.5' }} />
                                                Price List
                                            </Link>
                                            <Link
                                                to={item.cardViewPath}
                                                className="group flex items-center gap-2 px-4 py-2.5 rounded-xl
                                                           hover:-translate-y-0.5
                                                           transition-all duration-300 ease-out"
                                                style={{ 
                                                    backgroundColor: '#f5f5f7',
                                                    color: '#1d1d1f',
                                                    border: 'none',
                                                    fontWeight: '500',
                                                    fontSize: '15px',
                                                    letterSpacing: '-0.01em'
                                                }}
                                                onMouseEnter={(e) => {
                                                    e.currentTarget.style.backgroundColor = '#0071e3';
                                                    e.currentTarget.style.color = '#ffffff';
                                                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 113, 227, 0.3)';
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.currentTarget.style.backgroundColor = '#f5f5f7';
                                                    e.currentTarget.style.color = '#1d1d1f';
                                                    e.currentTarget.style.boxShadow = 'none';
                                                }}
                                            >
                                                <CreditCard className="w-5 h-5 group-hover:scale-110 transition-transform duration-300" style={{ strokeWidth: '1.5' }} />
                                                Card View
                                            </Link>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            {/* Licensed Brand 分组 */}
                            <div className="mt-6 pt-6" style={{ borderTop: '1px solid #e5e5e7' }}>
                                <div 
                                    className="mb-3 flex items-center gap-2" 
                                    style={{ 
                                        color: '#0071e3',
                                        fontWeight: '600',
                                        fontSize: '16px',
                                        letterSpacing: '-0.01em'
                                    }}
                                >
                                    <Tag className="w-5 h-5" style={{ strokeWidth: '1.5' }} />
                                    Licensed Brand
                                </div>
                                <div className="flex flex-col gap-2">
                                    {licensedBrands.map((brand) => (
                                        <Link
                                            key={brand.name}
                                            to={brand.path}
                                            className="flex items-center gap-2 px-4 py-2.5 rounded-xl transition-all duration-300 ease-out"
                                            style={{ 
                                                backgroundColor: '#f5f5f7',
                                                color: '#1d1d1f',
                                                border: 'none',
                                                fontWeight: '500',
                                                fontSize: '15px',
                                                letterSpacing: '-0.01em'
                                            }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.backgroundColor = '#0071e3';
                                                e.currentTarget.style.color = '#ffffff';
                                                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 113, 227, 0.3)';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.backgroundColor = '#f5f5f7';
                                                e.currentTarget.style.color = '#1d1d1f';
                                                e.currentTarget.style.boxShadow = 'none';
                                            }}
                                        >
                                            <Shield className="w-5 h-5" style={{ strokeWidth: '1.5' }} />
                                            {brand.name}
                                        </Link>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                    {/* 右侧 */}
                    <div className="flex-1 flex flex-col">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 flex-1">
                            {rightTools.map((tool) => (
                                <Link
                                    key={tool.name}
                                    to={tool.path}
                                    className="p-6 rounded-2xl
                                               hover:-translate-y-1
                                               transition-all duration-300 ease-out"
                                    style={{ 
                                        backgroundColor: '#ffffff',
                                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04), 0 1px 2px rgba(0, 0, 0, 0.06)',
                                        border: 'none'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.boxShadow = '0 8px 24px rgba(0, 0, 0, 0.12), 0 2px 4px rgba(0, 0, 0, 0.08)';
                                        e.currentTarget.style.transform = 'translateY(-4px)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.04), 0 1px 2px rgba(0, 0, 0, 0.06)';
                                        e.currentTarget.style.transform = 'translateY(0)';
                                    }}
                                >
                                    <div className="flex items-center space-x-3 mb-2.5">
                                        <div style={{ color: '#0071e3' }}>
                                            {tool.icon}
                                        </div>
                                        <h2 
                                            className="font-semibold" 
                                            style={{ 
                                                color: '#1d1d1f',
                                                fontWeight: '600',
                                                fontSize: '17px',
                                                letterSpacing: '-0.01em',
                                                lineHeight: '1.4'
                                            }}
                                        >
                                            {tool.name}
                                        </h2>
                                    </div>
                                    <p 
                                        className="text-sm" 
                                        style={{ 
                                            color: '#6e6e73',
                                            fontSize: '14px',
                                            lineHeight: '1.5',
                                            letterSpacing: '-0.01em'
                                        }}
                                    >
                                        {tool.description}
                                    </p>
                                </Link>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Dashboard;